import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useDeleteContact } from '../../../sic-lib/api/hooks/useContact';
import { requestContacts } from '../../../store/conversationGrid';
import { useAppDispatch } from '../../../store/hooks';
import ButtonLoading from '../../Shared/ButtonLoading';

const useStyles = makeStyles()((theme: Theme) => ({
    btnCancel: {
        marginRight: theme.spacing(1),
        color: theme.palette.btnDefault.main,
        '&:hover': {
            backgroundColor: theme.palette.btnDefault.light,
        },
    },
}));

interface Props {
    isOpen: boolean;
    contactId: number;
    onDoneHandler: () => void;
    onDeleteHandler?: () => void;
}

export default function DialogConfirmDeleteContact({
    isOpen,
    contactId,
    onDoneHandler,
    onDeleteHandler,
}: Props) {
    const { classes } = useStyles();
    const dispatch = useAppDispatch();

    const { mutate: deleteContact, isPending: isLoading } = useDeleteContact(contactId);

    const handleCancel = (event: React.MouseEvent<HTMLButtonElement>, reason?: string) => {
        if (!reason || (reason !== 'backdropClick' && reason !== 'escapeKeyDown')) {
            onDoneHandler();
        }
    };

    const handleConfirm = async () => {
        deleteContact();

        if (onDeleteHandler) {
            onDeleteHandler();
        } else {
            /*
                IMPORTANT: this will work so long as this dialog is only opened from somewhere
                within the conversationGrid component tree; request conversationGrid contacts to 
                reflect removed contact
             */
            dispatch(requestContacts());

            onDoneHandler();
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleCancel}
            maxWidth="xs"
            aria-labelledby="dialog_confirm_action"
        >
            <DialogTitle id="dialog_confirm_action">Confirm Delete Contact</DialogTitle>
            <DialogContent dividers>
                <Typography component="p" variant="body1">
                    This contact will be permanently deleted
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button
                    className={classes.btnCancel}
                    onClick={handleCancel}
                    variant="text"
                    autoFocus
                >
                    Cancel
                </Button>
                <ButtonLoading
                    isLoading={isLoading}
                    onClick={handleConfirm}
                    color="secondary"
                    variant="outlined"
                >
                    Ok
                </ButtonLoading>
            </DialogActions>
        </Dialog>
    );
}
